import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from './MobileApp';
import { mobileApi } from '../../services/api';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';
import MobileNav from '../../components/mobile/MobileNav';
import MobileHeader from '../../components/mobile/MobileHeader';
import MobileCard from '../../components/mobile/MobileCard';
import { AlertTriangle, ChevronRight, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';

const PAGE = 20;
const DOT = { critical: '#C23A00', high: '#C23A00', medium: '#E0B800', low: '#8A857A' };
const SPINE = { critical: 'var(--critical)', high: 'var(--overdue)', medium: 'var(--overdue)', low: 'var(--border)' };
const daysSince = (d) => (d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : null);
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

const DefectCard = ({ defect }) => {
  const navigate = useNavigate();
  const sev = (defect.severity || '').toLowerCase();
  const asset = defect.asset_unit_number || defect.asset_name || 'No asset';
  const age = daysSince(defect.created_at);
  const source = defect.reported_by_name || 'From inspection';
  return (
    <MobileCard spine={SPINE[sev] || 'var(--border)'} className="mb-2.5" padding="normal"
      onClick={() => navigate(`/mobile/defects/${defect.id}`)} data-testid={`defect-card-${defect.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-mono text-[14px] font-bold text-[var(--ink-primary)] truncate">{asset}</span>
            {sev === 'critical' && <span className="inline-flex items-center px-1.5 py-0.5 rounded-[4px] text-[11px] font-semibold border bg-[var(--critical-bg)] text-[var(--critical-ink)] border-[var(--critical-edge)]">CRITICAL</span>}
          </div>
          <div className="text-[15px] font-semibold text-[var(--ink-primary)] truncate mt-0.5">{defect.title}</div>
          <div className="flex items-center gap-1.5 mt-1.5 text-[13px] min-w-0">
            <span className="shrink-0 rounded-full" style={{ width: 6, height: 6, background: DOT[sev] || '#8A857A' }} />
            {age != null && <span className="font-semibold shrink-0" style={{ color: sev === 'critical' ? 'var(--critical-ink)' : sev === 'low' ? 'var(--ink-tertiary)' : 'var(--overdue-ink)' }}>{age} {age === 1 ? 'day' : 'days'} open</span>}
            <span className="text-[var(--ink-tertiary)] truncate">· {source}</span>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-[var(--ink-tertiary)] shrink-0" />
      </div>
    </MobileCard>
  );
};

const MobileDefects = () => {
  const navigate = useNavigate();
  const { getToken } = useMobileAuth();
  const [filter, setFilter] = useState('open');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ open: 0, critical: 0, resolved: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const offsetRef = useRef(0);

  const fetchPage = useCallback(async (f, q, offset, replace) => {
    try {
      const res = await mobileApi.getDefects(getToken(), f, q, PAGE, offset);
      const data = res.data;
      setTotal(data.total || 0);
      if (data.counts) setCounts(data.counts);
      setItems((prev) => replace ? (data.items || []) : [...prev, ...(data.items || [])]);
      offsetRef.current = offset + (data.items || []).length;
    } catch { toast.error('Failed to load defects'); }
  }, [getToken]);

  useEffect(() => {
    setLoading(true);
    offsetRef.current = 0;
    const t = setTimeout(() => { fetchPage(filter, search, 0, true).finally(() => setLoading(false)); }, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [filter, search, fetchPage]);

  const hasMore = items.length < total;
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    await fetchPage(filter, search, offsetRef.current, false);
    setLoadingMore(false);
  }, [loadingMore, hasMore, fetchPage, filter, search]);
  const sentinelRef = useInfiniteScroll(hasMore, loading || loadingMore, loadMore);

  const tabs = [{ key: 'open', label: 'Open' }, { key: 'critical', label: 'Critical' }, { key: 'resolved', label: 'Resolved' }];

  // Group by real severity (only meaningful within Open/Resolved views).
  const rendered = [];
  let lastSev = null;
  const SEV_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };
  const sorted = filter === 'critical' ? items : [...items].sort((a, b) => (SEV_ORDER[a.severity] ?? 9) - (SEV_ORDER[b.severity] ?? 9));
  sorted.forEach((d) => {
    const sev = (d.severity || 'medium').toLowerCase();
    if (filter !== 'critical' && sev !== lastSev) {
      rendered.push(
        <div key={`h-${d.id}`} className="flex items-center gap-3 mb-3 mt-1">
          <span className="text-[11px] font-bold uppercase tracking-[0.11em]" style={{ color: sev === 'critical' ? 'var(--critical-ink)' : sev === 'low' ? '#6E6A61' : 'var(--overdue-ink)' }}>{sev}</span>
          <div className="h-px flex-1 bg-[var(--border)]" />
        </div>
      );
      lastSev = sev;
    }
    rendered.push(<DefectCard key={d.id} defect={d} />);
  });

  return (
    <div className="min-h-screen bg-[var(--surface-page)] pb-24" data-testid="mobile-defects-page">
      <MobileHeader title="Defects" showBack />

      <div className="px-4 py-4">
        <div className="flex gap-1 p-1 rounded-[12px] bg-[var(--surface-sunken)] border border-[var(--border)]" style={{ height: 44 }}>
          {tabs.map((t) => {
            const sel = filter === t.key;
            return (
              <button key={t.key} onClick={() => setFilter(t.key)} data-testid={`defects-filter-${t.key}`}
                className={`flex-1 rounded-[9px] flex items-center justify-center gap-1.5 text-[13px] transition-colors ${sel ? 'bg-white font-bold text-[var(--ink-primary)]' : 'text-[var(--ink-secondary)] font-medium'}`}
                style={sel ? { border: '1px solid #C4BFB3' } : {}}>{t.label}<span className="font-mono text-[11px] text-[var(--ink-tertiary)]">{counts[t.key]}</span></button>
            );
          })}
        </div>

        <div className="relative mt-3">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ink-tertiary)]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search defects or assets"
            data-testid="defects-search"
            className="w-full h-[46px] rounded-[12px] bg-white border border-[var(--border-strong)] pl-10 pr-4 text-[16px] text-[var(--ink-primary)] outline-none focus:border-[#1A1A1A] focus:shadow-[0_0_0_3px_rgba(255,209,8,0.35)]" />
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[var(--volt-icon)] animate-spin" /></div>
        ) : items.length === 0 ? (
          <MobileCard className="text-center py-8 mt-4">
            <AlertTriangle className="w-10 h-10 text-[var(--ink-tertiary)] mx-auto mb-2" />
            <p className="text-[15px] font-semibold text-[var(--ink-secondary)]">No {filter} defects</p>
          </MobileCard>
        ) : (
          <div className="mt-4">
            {rendered}
            <div ref={sentinelRef} />
            {loadingMore && <div className="flex justify-center py-4"><Loader2 className="w-6 h-6 text-[var(--volt-icon)] animate-spin" /></div>}
            {!hasMore && <p className="text-center text-[12px] text-[var(--ink-tertiary)] py-3">{total} defect{total === 1 ? '' : 's'}</p>}
          </div>
        )}

        {/* Report new defect — secondary, above the nav (no floating +) */}
        <button onClick={() => navigate('/mobile/defects/new')} data-testid="report-defect-btn"
          className="w-full flex items-center justify-center gap-2 mt-2 bg-white rounded-[13px]" style={{ height: 52, border: '1.5px solid #1A1A1A' }}>
          <AlertTriangle className="w-[18px] h-[18px] text-[var(--ink-primary)]" />
          <span className="text-[14px] font-semibold text-[var(--ink-primary)]">Report new defect</span>
        </button>
      </div>

      <MobileNav />
    </div>
  );
};

export default MobileDefects;
